// trip detail helpers
import { Program, User } from "src/modules/auth/types";
import {
    Fleetmanager,
    FormattedGPS,
    OnDemandTrip,
    PassioBus,
    PassioDriver,
    PassioRun,
    RideRequest,
    RideStatus,
    TransitExperience,
    Vehicle,
} from "../../types";
import { capitalize, startCase, shuffle, min } from "lodash";
import {
    defaultTripStatus,
    defaultTripStatusCoords,
} from "src/utils/defaultData";
import { TripScheduleStatus } from "src/types";
import { SelectionType } from "../TripRequest/types";
import { RiderExperience } from "./types";
import { sortOnDemandTripActions } from "../../redux/helpers";
import { isValidCoord } from "src/utils/helpers";
import moment from "moment";

export function getParaplanStatusText(rideStatus: number | undefined) {
    switch (rideStatus) {
        case 1:
            return "Waiting for approval";
        case 2:
            return "Approved. Waiting for assignment";
        case 3:
        case 4:
            return "Assigned to driver";
        case 5:
            return "Driver on their way";
        case 6:
            return "Driver is almost there";
        case 7:
            return "Driver is at pick up location";
        case 8:
            return "Heading to drop off location";
        case 9:
            return "Driver is almost at drop off location";
        case 10:
            return "Driver is at drop off location";
        case 11:
            return "Trip has been completed";
        case 12:
            return "Trip has been rejected";
        case 13:
            return "Trip has been cancelled";
        default:
            return "Email confirmation is on the way!";
    }
}

export function getNavigatorStatusText(rideStatus: number | undefined) {
    switch (rideStatus) {
        case TripScheduleStatus.WaitingForApproval:
            return "Waiting for approval";
        case TripScheduleStatus.AcceptedTripRequest:
            return "Approved. Waiting for assignment";
        case TripScheduleStatus.Scheduled:
            return "Assigned to driver";
        case TripScheduleStatus.Autoscheduled:
            return "Assigned to driver";
        case TripScheduleStatus.PickUpArrived:
            return "Driver is at pick up location";
        case TripScheduleStatus.DropOffArrived:
            return "Driver is at drop off location";
        case TripScheduleStatus.DropOffPerformed:
            return "Trip has been completed";
        case TripScheduleStatus.NotApproved:
            return "Trip has been rejected";
        case TripScheduleStatus.Cancelled:
            return "Trip has been cancelled";
        default:
            return "Email confirmation is on the way!";
    }
}

export function formatAddress(address: any) {
    if (!address && !address?.name && !address?.city) {
        return "N/A";
    }
    return `${address.name}, ${address.city}`;
}

export function formatAddressFromRequest(
    request: RideRequest,
    type: SelectionType = "pickUp"
) {
    if (!request) return "N/A";
    else if (request[`${type}AddressFull`])
        return request[`${type}AddressFull`];
    return `${request[`${type}Address`]}, ${request[`${type}City`]}`;
}

export function formatTime(time: any, isNavigator = false) {
    if (!time) return "";
    return isNavigator
        ? moment(time).format("LLLL")
        : moment.unix(parseInt(time)).format("LLLL");
}

export function formatName(request: any) {
    if (!request && !request?.clientFirstName && !request?.clientLastName)
        return "";
    return `${capitalize(request.clientFirstName)} ${startCase(
        capitalize(request.clientLastName)
    )}`;
}

export function formatParaplanCoords(data: RideStatus | undefined) {
    if (!data) return defaultTripStatusCoords;

    let coords = { ...defaultTripStatusCoords };
    // default to request coords
    if (data.request) {
        coords = {
            ...coords,
            pickUpCoords: {
                lat: parseFloat(data.request.pickUpLatitude as any),
                lng: parseFloat(data.request.pickUpLongitude as any),
            },
            dropOffCoords: {
                lat: parseFloat(data.request.dropOffLatitude as any),
                lng: parseFloat(data.request.dropOffLongitude as any),
            },
        };
    }
    // add geo coords if available
    if (data?.geo) {
        if (data.geo?.pickUp) {
            coords.pickUpCoords = data.geo.pickUp;
        }
        if (data.geo?.dropOff) {
            coords.dropOffCoords = data.geo.dropOff;
        }
        if (data.geo?.vehicle) {
            coords.vehicleCoords = data.geo.vehicle;
        }
        if (data.geo?.rider) {
            coords.riderCoords = data.geo.rider;
        }
    }
    return coords;
}

export function formatParaplanRiderExperience(
    data: RideStatus | undefined
): RiderExperience {
    if (!data) return defaultTripStatus;

    const pickUpAddress = data?.geo?.pickUp
        ? formatAddress(data.geo.pickUp)
        : formatAddressFromRequest(data.request);

    const dropOffAddress = data?.geo?.dropOff
        ? formatAddress(data.geo.dropOff)
        : formatAddressFromRequest(data.request, "dropOff");

    const scheduledPickUpTimeEpoch = data?.times?.scheduledPickUpTimeEpoch
        ? formatTime(data?.times?.scheduledPickUpTimeEpoch)
        : formatTime(data?.request?.pickUpTimeEpoch);

    const scheduledDropOffTimeEpoch = data?.times?.scheduledDropOffTimeEpoch
        ? formatTime(data?.times?.scheduledDropOffTimeEpoch)
        : formatTime(data?.request?.dropOffTimeEpoch);

    let details = {
        ...defaultTripStatus,
        tripId: data.tripId,
        pickUpAddress,
        dropOffAddress,
        scheduledPickUpTimeEpoch,
        rideStatus: data.rideStatus,
        scheduledDropOffTimeEpoch,
        status: getParaplanStatusText(data.rideStatus),
        coords: formatParaplanCoords(data),
        username: formatName(data.request),
        // only add times when available
        ...(data?.times && {
            actualPickUpArrivalEpoch: formatTime(
                data?.times?.actualPickUpArrivalEpoch
            ),
            estimatedPickUpArrivalEpoch: formatTime(
                data?.times?.estimatedPickUpArrivalEpoch
            ),
            actualDropOffTimePerformEpoch: formatTime(
                data?.times?.actualDropOffTimePerformEpoch
            ),
            estimatedDropOffArrivalEpoch: formatTime(
                data?.times?.estimatedDropOffArrivalEpoch
            ),
            estimatedDropOffTimePerformEpoch: formatTime(
                data?.times?.estimatedDropOffTimePerformEpoch
            ),
        }),
    };

    return details;
}

export function formatNavigatorRiderExperience(
    trip: OnDemandTrip,
    user: User,
    bus: PassioBus | undefined
): RiderExperience {
    if (!trip || !user) return defaultTripStatus;

    /**
     * Use updated trip statuses from onDemandTripActionHistory to assign the correct
     * reservationType for each trip
     * Sort first to get the correct entry
     */
    const sortedLog = sortOnDemandTripActions(trip.onDemandTripActionHistory);
    const actionId = sortedLog[0]
        ? parseInt(sortedLog[0].onDemandActionId)
        : TripScheduleStatus.WaitingForApproval;

    const pickUpArrived =
        trip.actualPickUpArrivalTime ||
        actionId === TripScheduleStatus.PickUpArrived;
    const dropOffArrived =
        trip.actualDropOffArrivalTime ||
        actionId === TripScheduleStatus.DropOffArrived;

    const isNoShow = trip.noShow || actionId === TripScheduleStatus.Noshowed;
    const isRejected = actionId === TripScheduleStatus.NotApproved;
    const isScheduled = actionId === TripScheduleStatus.Scheduled;
    const isAutoScheduled = actionId === TripScheduleStatus.Autoscheduled;
    const isCancelled =
        trip.canceled || actionId === TripScheduleStatus.Cancelled;
    const isApprovedButNotAssigned =
        actionId === TripScheduleStatus.AcceptedTripRequest && !trip.runId;

    const rideStatus =
        isCancelled || isRejected || isNoShow
            ? TripScheduleStatus.Cancelled
            : trip.actualDropOffTime
            ? TripScheduleStatus.DropOffPerformed
            : trip.actualPickUpTime
            ? TripScheduleStatus.PickUpPerformed
            : dropOffArrived
            ? TripScheduleStatus.DropOffArrived
            : pickUpArrived
            ? TripScheduleStatus.PickUpArrived
            : trip.runId || isScheduled || isAutoScheduled
            ? TripScheduleStatus.Scheduled
            : !trip.requested || isApprovedButNotAssigned
            ? TripScheduleStatus.AcceptedTripRequest
            : actionId;

    const pickUpPlace = JSON.parse(trip.pickUpPlace);
    const dropOffPlace = JSON.parse(trip.dropOffPlace);
    // todo: use autoSchedule fields to show warning message when retry endpoint is ready
    let details = {
        ...defaultTripStatus,
        rideStatus,
        runId: trip.runId || "",
        tripId: trip.id,
        tripURL: trip.tripURL,
        tripUuid: trip.tripUuid,
        riderId: trip.riderId,
        serviceId: trip.serviceId,
        requesterEmail: trip.requesterEmail,
        pickUpTime: trip.pickUpTime,
        dropOffTime: trip.dropOffTime,
        username: user.Name,
        pickUpAddress: formatAddress(pickUpPlace),
        dropOffAddress: formatAddress(dropOffPlace),
        scheduledPickUpTimeEpoch: formatTime(trip.pickUpTime, true),
        scheduledDropOffTimeEpoch: formatTime(trip.dropOffTime, true),
        status: getNavigatorStatusText(rideStatus),
        tripRating: trip.tripRating ? parseInt(trip.tripRating) : 0,
        shouldAutoSchedule: trip.shouldAutoSchedule ? true : false,
        autoScheduleStatus: trip.autoScheduleStatus || null,
        coords: {
            ...defaultTripStatus.coords,
            pickUpCoords: {
                lat: parseFloat(pickUpPlace.latitude),
                lng: parseFloat(pickUpPlace.longitude),
            },
            dropOffCoords: {
                lat: parseFloat(dropOffPlace.latitude),
                lng: parseFloat(dropOffPlace.longitude),
            },
            // add vehicle coords if bus is available
            ...(bus && {
                vehicleCoords: {
                    lat: parseFloat(bus.latitude),
                    lng: parseFloat(bus.longitude),
                },
            }),
        },
    };

    return details;
}

export async function getDuration(
    origin: string,
    destination: string
): Promise<any> {
    try {
        let duration = 0;
        const service = new google.maps.DirectionsService();
        if (!service) return;
        await service.route(
            {
                origin,
                destination,
                travelMode: google.maps.TravelMode.DRIVING,
            },
            (res, status) => {
                if (status === "OK" && res) {
                    duration = res.routes[0].legs[0].duration?.value || 0;
                }
            }
        );
        return duration;
    } catch {}
}

function formatETA(duration: number) {
    const time = moment().add(duration, "seconds");
    return `${time.clone().subtract(2, "m").format("LT")} - ${time
        .clone()
        .add(5, "m")
        .format("LT")}`;
}

export async function formatTransitExperience(
    trip: OnDemandTrip,
    run: PassioRun | undefined,
    bus: PassioBus | undefined,
    driver: PassioDriver | undefined,
    manifest: OnDemandTrip[] = []
): Promise<TransitExperience> {
    const pickUpPlace = JSON.parse(trip.pickUpPlace);
    const dropOffPlace = JSON.parse(trip.dropOffPlace);
    const isValid = !(trip.actualDropOffTime || trip.canceled || trip.noShow);

    let transit = {
        id: trip.id,
        runId: trip.runId || "",
        run,
        driverId: trip.driverId,
        driver,
        busId: trip.busId || "",
        bus,
        isValid,
        pickUpTime: trip.pickUpTime,
        dropOffTime: trip.dropOffTime,
        actualPickUpTime: trip.actualPickUpTime,
        actualDropOffTime: trip.actualDropOffTime,
        pickUpAddress: formatAddress(pickUpPlace),
        dropOffAddress: formatAddress(dropOffPlace),
        shouldAutoSchedule: trip.shouldAutoSchedule ? true : false,
        autoScheduleStatus: trip.autoScheduleStatus || null,
        autoUpdateManifest: run && run.autoUpdateManifest ? true : false,
        coords: {
            ...defaultTripStatusCoords,
            pickUpCoords: {
                lat: parseFloat(pickUpPlace.latitude),
                lng: parseFloat(pickUpPlace.longitude),
            },
            dropOffCoords: {
                lat: parseFloat(dropOffPlace.latitude),
                lng: parseFloat(dropOffPlace.longitude),
            },
            // add vehicle coords if bus is available
            ...(bus && {
                vehicleCoords: {
                    lat: parseFloat(bus.latitude),
                    lng: parseFloat(bus.longitude),
                },
            }),
        },
        ETA: 0,
        ETADisplay: "",
        isNext: false,
    };
    // if the trip is next
    if (bus && manifest.length === 1) {
        const duration = await getDuration(
            `${bus.latitude},${bus.longitude}`,
            `${pickUpPlace.latitude},${pickUpPlace.longitude}`
        );
        if (duration) {
            transit.isNext = true;
            transit.ETA = duration;
            transit.ETADisplay = formatETA(duration);
        }
    } else if (bus && manifest.length > 1) {
        let durations: any[] = [];

        for (const item of manifest) {
            const address = item.actualPickUpTime
                ? JSON.parse(item.dropOffPlace)
                : JSON.parse(item.pickUpPlace);

            const duration = await getDuration(
                `${bus.latitude},${bus.longitude}`,
                `${address.latitude},${address.longitude}`
            );
            durations.push(duration);
        }

        const closestByTime = await min(durations);
        const index = durations.findIndex((item) => closestByTime);
        const closest = manifest[index];

        if (closest.id === trip.id) {
            transit.isNext = true;
            transit.ETA = closestByTime;
            transit.ETADisplay = formatETA(closestByTime);
        } else {
            const tripIndex = manifest.findIndex((item) => item.id === trip.id);
            const totalTime = Array.from(durations)
                .filter((item, index) => index !== tripIndex)
                .reduce((acc, item) => acc + item, 0);

            transit.ETA = totalTime;
            transit.ETADisplay = formatETA(totalTime);
        }
    }
    return transit;
}

export function getDistanceBetweenCoords(
    from: google.maps.LatLngLiteral,
    to: google.maps.LatLngLiteral
) {
    try {
        const fromCoords = new google.maps.LatLng(from);
        const toCoords = new google.maps.LatLng(to);
        return google.maps.geometry.spherical.computeDistanceBetween(
            fromCoords,
            toCoords
        );
    } catch (error) {
        console.error("Error calculating distance:", error);
        return 0;
    }
}

// convert distance in meters to miles
export function distanceToMiles(distance = 0) {
    return Math.floor(distance * 0.000621371);
}

export interface RankedVehicle {
    points: number;
    vehicleId: number | string;
    onboardRiders: number;
    futureRiders: number;
    totalCap: number;
    runId: number;
    driverId: number | string;
    selected: boolean;
    trips: (string | number)[];
    lat: number | null;
    lng: number | null;
}

export async function autoSchedule(
    tripId: string | number = "",
    runs: Fleetmanager[] = [],
    vehicles: Vehicle[] = [],
    drivers: PassioDriver[] = [],
    trips: OnDemandTrip[] = [],
    currentGPS: FormattedGPS[] = [],
    services: Program[] = [],
    debug = false
): Promise<RankedVehicle[]> {
    let rankedVehicles: RankedVehicle[] = [];
    const trip = trips.find((trip) => trip.id === String(tripId));

    if (!trip) return [];
    // points are parsed and defaults set when services are first loaded
    const points = services.find((item) => item.databaseID === trip.serviceId)
        ?.autoScheduleSettings;

    if (!points) return [];

    if (debug) {
        console.log("🚀 ~ AutoScheduling trip:", trip);
    }

    for (const run of runs) {
        // skip runs with unassigned drivers or vehicles
        if (!run.driverID || !run.vehicleID) continue;
        const vehicle = vehicles.find(
            (vehicle) => vehicle.databaseID === run.vehicleID
        );
        // if vehicle does not exist, skip
        if (!vehicle) continue;
        // check if driver is paused
        const driver = drivers.find(
            (driver) => Number(driver.id) === run.driverID
        );
        if (!driver || driver.onDemandIsPaused) continue;
        const gps = currentGPS.find((gps) => gps.busId === run.vehicleID);
        const assignedTrips = trips.filter((trip) => {
            return trip.runId && parseInt(trip.runId) === run.fleetmanagerID;
        });

        let ranking = {
            points: 0,
            vehicleId: vehicle.databaseID || 0,
            totalCap: vehicle.totalCap || 0,
            onboardRiders: 0,
            futureRiders: 0,
            runId: run.fleetmanagerID,
            driverId: run.driverID,
            selected: false,
            trips: assignedTrips.length
                ? assignedTrips.map((trip) => trip.id)
                : [],
            lat: gps?.lat || null,
            lng: gps?.lng || null,
        };

        // if no current trip, add 10 points
        if (!assignedTrips.length) {
            ranking.points += points.noCurrentTripValue;
        } else {
            // check capacity
            const tripUnloaded = moment(trip.unloadedTime);
            const tripPickUpTime = moment(trip.pickUpTime);
            for (const item of assignedTrips) {
                const now = moment();
                const pickUpTime = moment(item.pickUpTime);
                const unloadedTime = moment(item.unloadedTime);
                const totalRiders = item.totalRiders
                    ? parseInt(item.totalRiders)
                    : 1;
                if (
                    pickUpTime.isBefore(now) &&
                    now.isBefore(unloadedTime) &&
                    tripPickUpTime.isBetween(pickUpTime, unloadedTime)
                ) {
                    ranking.onboardRiders += totalRiders;
                }

                if (
                    tripPickUpTime.isSameOrAfter(pickUpTime) &&
                    tripUnloaded.isSameOrBefore(item.unloadedTime)
                ) {
                    ranking.onboardRiders += totalRiders;
                }

                if (
                    tripUnloaded.isAfter(pickUpTime) &&
                    unloadedTime.isBetween(now, tripUnloaded)
                ) {
                    ranking.futureRiders += totalRiders;
                }
            }
            // check if no trip is pending we can deduce this from onboard riders
            if (!ranking.onboardRiders) {
                ranking.points += points.noPendingTripsValue;
            }
        }

        // if vehicle is not moving or speed is lower than 5mph, add 10 points
        if (!gps || gps?.speed < 5) {
            ranking.points += points.vehicleIdleValue;
        }

        // if run has valid gps coordinates, calculate distance to trip
        if (gps && isValidCoord(gps)) {
            //  calculate distance to pickup
            const distance = getDistanceBetweenCoords(
                gps,
                JSON.parse(trip.pickUpPlace)
            );
            // if distance is under 10miles, add the difference as points
            const pointDiff =
                points.currentLocationDistanceMaxValue -
                distanceToMiles(distance);
            if (
                pointDiff > 0 &&
                pointDiff < points.currentLocationDistanceMaxValue
            ) {
                ranking.points += pointDiff;
            }

            if (debug) {
                console.info(
                    "🚀 ~ Calculating point difference, distance to pickup"
                );
                console.log("🚀 ~ distance:", distance);
                console.log("🚀 ~ pointDif:", pointDiff);
            }

            // if vehicle has other trips, calculate distance from last trip manifest to vehicles current location

            if (assignedTrips.length) {
                const lastTrip = assignedTrips.pop();
                if (lastTrip) {
                    const distance = getDistanceBetweenCoords(
                        gps,
                        JSON.parse(trip.dropOffPlace)
                    );

                    // if distance is under 10miles, add the difference as points
                    const pointDiff =
                        points.nextLocationDistanceMaxValue -
                        distanceToMiles(distance);
                    if (
                        pointDiff > 0 &&
                        pointDiff < points.nextLocationDistanceMaxValue
                    ) {
                        ranking.points += pointDiff;
                    }

                    if (debug) {
                        console.info(
                            "🚀 ~ Run has no pending trips, calculating point difference"
                        );
                        console.log("🚀 ~ distance:", distance);
                        console.log("🚀 ~ pointDif:", pointDiff);
                    }
                }
            }
        }

        rankedVehicles.push(ranking);

        if (debug) {
            console.log(
                `🚀 ~ run: ${run.name || run.nameWithTP} | ${run.vehicleName}`,
                run
            );
            console.log(`🚀 ~ vehicle: ${vehicle.name}`, vehicle);
            console.log(`🚀 ~ gps: ${gps?.busId} | ${gps?.driver}`, gps);
            console.log("🚀 ~ assignedTrips ~ assignedTrips:", assignedTrips);
            console.log("🚀 ~  ranking :", ranking);
        }
    }

    // sort vehicles by points in descending order
    rankedVehicles = rankedVehicles.sort((a, b) => b.points - a.points);
    // check if mulitple eligible vehicles at the top have the same points
    const eligibleDuplicates = rankedVehicles.filter(
        (item) => item.points === rankedVehicles[0]?.points
    );
    const totalRiders = trip.totalRiders ? parseInt(trip.totalRiders) : 1;
    // try finding a match in the available list first
    if (eligibleDuplicates.length >= 2) {
        // shuffle to ensure randomization
        const shuffled = shuffle(eligibleDuplicates);
        for (let i = 0; i < shuffled.length; i++) {
            const vehicle = shuffled[i];
            const potentialRiderCount =
                vehicle.onboardRiders + vehicle.futureRiders + totalRiders;
            if (vehicle.totalCap >= potentialRiderCount) {
                // we only want one selected target
                shuffled[i].selected = true;
                break;
            }
        }
        // update rankings keeping the selected in order
        rankedVehicles = shuffled.concat(
            rankedVehicles.splice(shuffled.length)
        );
    }

    // if no target is found from the short list, check if other ranked vehicles have capacity and select best
    if (!rankedVehicles.find((item) => item.selected)) {
        // prioritize vehicles with more available capacity
        rankedVehicles = rankedVehicles.reduce(
            (acc: RankedVehicle[] = [], vehicle) => {
                const potentialRiderCount =
                    vehicle.onboardRiders + vehicle.futureRiders + totalRiders;
                if (vehicle.totalCap >= potentialRiderCount) {
                    // we only want one selected target
                    if (!acc.find((item) => item.selected)) {
                        vehicle.selected = true;
                    }
                }
                acc.push(vehicle);
                return acc;
            },
            []
        );
    }

    // if no best fit target was found, check if first vehicle will be free at the time this trip will get on
    if (
        rankedVehicles.length &&
        !rankedVehicles.find((item) => item.selected)
    ) {
        const topRated = rankedVehicles[0];
        const lastTrip = trips.find((item) => item.id === topRated.trips[0]);
        if (
            lastTrip &&
            moment(lastTrip.unloadedTime).isBefore(moment(trip.pickUpTime))
        ) {
            topRated.selected = true;
        }
    }

    if (debug) {
        console.group("🚀 ~ RankedVehicles");
        for (const item of rankedVehicles) {
            const vehicle = vehicles.find(
                (vehicle) => vehicle.databaseID === item.vehicleId
            );
            console.log(
                `🚀 ~ ${
                    item.selected ? "AutoScheduleTarget" : "Vehicle"
                }: ${vehicle?.name} | ${item.points}`,
                item
            );
        }
        console.groupEnd();
    }

    return rankedVehicles;
}

export const notificationCheckKeys = {
    driverArrived: false,
    tripApproved: false,
    tripNotApproved: false,
    tripScheduled: false,
};

export function getNotificationLSKey(
    id: string | number,
    type: "sms" | "email" = "email"
) {
    return type === "sms" ? `${id}smsChecks` : `${id}emailChecks`;
}
